import { useEffect, useRef, useState } from "react";
import { Accessibility, X, Contrast, ZapOff, Underline, RotateCcw } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { useI18n } from "@/i18n";

const STRINGS = {
  en: { title: "Accessibility", fontSize: "Text size", highContrast: "High contrast", reduceMotion: "Reduce motion", underlineLinks: "Underline links", reset: "Reset", open: "Accessibility options", close: "Close" },
  pl: { title: "Dostępność", fontSize: "Rozmiar tekstu", highContrast: "Wysoki kontrast", reduceMotion: "Ogranicz animacje", underlineLinks: "Podkreśl linki", reset: "Resetuj", open: "Opcje dostępności", close: "Zamknij" },
  de: { title: "Barrierefreiheit", fontSize: "Textgröße", highContrast: "Hoher Kontrast", reduceMotion: "Animationen reduzieren", underlineLinks: "Links unterstreichen", reset: "Zurücksetzen", open: "Barrierefreiheit-Optionen", close: "Schließen" },
  es: { title: "Accesibilidad", fontSize: "Tamaño del texto", highContrast: "Alto contraste", reduceMotion: "Reducir animaciones", underlineLinks: "Subrayar enlaces", reset: "Restablecer", open: "Opciones de accesibilidad", close: "Cerrar" },
  fr: { title: "Accessibilité", fontSize: "Taille du texte", highContrast: "Contraste élevé", reduceMotion: "Réduire les animations", underlineLinks: "Souligner les liens", reset: "Réinitialiser", open: "Options d'accessibilité", close: "Fermer" },
  it: { title: "Accessibilità", fontSize: "Dimensione testo", highContrast: "Alto contrasto", reduceMotion: "Riduci animazioni", underlineLinks: "Sottolinea link", reset: "Reimposta", open: "Opzioni di accessibilità", close: "Chiudi" },
  pt: { title: "Acessibilidade", fontSize: "Tamanho do texto", highContrast: "Alto contraste", reduceMotion: "Reduzir animações", underlineLinks: "Sublinhar links", reset: "Repor", open: "Opções de acessibilidade", close: "Fechar" },
  tr: { title: "Erişilebilirlik", fontSize: "Yazı boyutu", highContrast: "Yüksek kontrast", reduceMotion: "Animasyonları azalt", underlineLinks: "Bağlantıların altını çiz", reset: "Sıfırla", open: "Erişilebilirlik seçenekleri", close: "Kapat" },
  ru: { title: "Доступность", fontSize: "Размер текста", highContrast: "Высокий контраст", reduceMotion: "Меньше анимаций", underlineLinks: "Подчёркивать ссылки", reset: "Сбросить", open: "Настройки доступности", close: "Закрыть" },
  ar: { title: "إمكانية الوصول", fontSize: "حجم النص", highContrast: "تباين عالٍ", reduceMotion: "تقليل الحركة", underlineLinks: "تسطير الروابط", reset: "إعادة تعيين", open: "خيارات إمكانية الوصول", close: "إغلاق" },
};

const KEY = "ns_a11y";
const TOGGLES = [
  { id: "highContrast", cls: "a11y-high-contrast", icon: Contrast },
  { id: "reduceMotion", cls: "a11y-reduce-motion", icon: ZapOff },
  { id: "underlineLinks", cls: "a11y-underline-links", icon: Underline },
];

function load() {
  try { return { ...JSON.parse(localStorage.getItem(KEY) || "{}") }; } catch { return {}; }
}

function apply(s) {
  const root = document.documentElement;
  const fs = s.fontSize || 0;
  root.classList.toggle("a11y-text-1", fs === 1);
  root.classList.toggle("a11y-text-2", fs === 2);
  TOGGLES.forEach((o) => root.classList.toggle(o.cls, !!s[o.id]));
}

export function AccessibilityPanel({ openUp = false, showLabel = false }) {
  const { lang } = useI18n();
  const s = STRINGS[lang] || STRINGS.en;
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(load);
  const panelRef = useRef(null);

  useEffect(() => { apply(settings); try { localStorage.setItem(KEY, JSON.stringify(settings)); } catch { /* ignore */ } }, [settings]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    const onClick = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onClick); };
  }, [open]);

  const toggle = (id) => setSettings((p) => ({ ...p, [id]: !p[id] }));
  const setFont = (v) => setSettings((p) => ({ ...p, fontSize: v }));
  const reset = () => setSettings({});
  const fontLevel = settings.fontSize || 0;
  const active = TOGGLES.filter((o) => settings[o.id]).length + (fontLevel > 0 ? 1 : 0);

  return (
    <div ref={panelRef} className="relative print:hidden">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-label={s.open} aria-expanded={open} aria-haspopup="dialog" data-testid="a11y-fab"
        className={showLabel
          ? "relative flex h-10 items-center gap-2 rounded-full border border-border px-3 text-sm font-600 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          : "relative flex h-10 w-10 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"}>
        <Accessibility className="h-[18px] w-[18px]" />
        {showLabel && <span>{s.title}</span>}
        {active > 0 && (
          <span aria-hidden="true" className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-success px-1 text-[9px] font-800 text-white">{active}</span>
        )}
      </button>
      {open && (
        <div role="dialog" aria-label={s.title} data-testid="a11y-panel" className={`absolute z-[70] w-72 rounded-2xl border border-border bg-card p-4 shadow-xl ${openUp ? "bottom-12 left-0" : "top-12 right-0"}`}>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-head text-base font-700"><Accessibility className="h-5 w-5 text-primary" /> {s.title}</h2>
            <button type="button" onClick={() => setOpen(false)} aria-label={s.close} data-testid="a11y-close" className="rounded-full p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Font size slider (3 levels) */}
          <div className="rounded-xl px-2 py-2.5">
            <div className="mb-2 flex items-center justify-between">
              <span className="flex items-center gap-2.5 text-sm font-600"><span className="font-head font-800" aria-hidden="true">A</span> {s.fontSize}</span>
              <span className="text-xs font-700 text-muted-foreground">{["A", "A+", "A++"][fontLevel]}</span>
            </div>
            <Slider
              data-testid="a11y-fontsize-slider"
              aria-label={s.fontSize}
              value={[fontLevel]}
              min={0}
              max={2}
              step={1}
              onValueChange={([v]) => setFont(v)}
            />
          </div>

          <div className="mt-1 space-y-1">
            {TOGGLES.map((o) => {
              const Icon = o.icon;
              return (
                <label key={o.id} htmlFor={`a11y-${o.id}`} className="flex cursor-pointer items-center justify-between gap-3 rounded-xl px-2 py-2.5 hover:bg-accent">
                  <span className="flex items-center gap-2.5 text-sm font-600"><Icon className="h-4 w-4 text-muted-foreground" /> {s[o.id]}</span>
                  <Switch id={`a11y-${o.id}`} data-testid={`a11y-toggle-${o.id}`} checked={!!settings[o.id]} onCheckedChange={() => toggle(o.id)} />
                </label>
              );
            })}
          </div>

          <button type="button" onClick={reset} data-testid="a11y-reset" className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-border py-2 text-sm font-600 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
            <RotateCcw className="h-4 w-4" /> {s.reset}
          </button>
        </div>
      )}
    </div>
  );
}
